import { LocalDate, mondayThisWeek } from 'src/utilities/local-date';
import { reducer } from 'src/store/reducers/reducers';
import { configureStore } from '@reduxjs/toolkit';
import { uuid } from 'src/utilities/text';
import type { State } from 'src/store/types';
import { setToday } from 'src/store/actions/setToday';
import { getConfiguredCache } from './money-clip';
import { checklistPendingMiddleware } from './checklist-pending-middleware';
import ms from 'milliseconds';
import { debounce } from 'lodash';
import { trace } from 'src/utilities/trace';

export { BASE_PATH } from './config';

export { RecurrenceRule } from './recurrence-rule/recurrence-rule';

const IS_BROWSER = typeof window !== 'undefined' || typeof self !== 'undefined';
const fallback = (cb) => setTimeout(cb, 0);
const ric = typeof requestIdleCallback === 'undefined' ? fallback : requestIdleCallback;
const ignoreActions = [
  'UPDATE_PATH',
  'UPDATE_HELP_VIEWER_OPEN',
  'UPDATE_TUTORIAL_VIEWER_OPEN',
  'UPDATE_SIDE_CONTENT_OPEN',
  'UPDATE_CHAT_OPEN',
  'UPDATE_CHAT_MODE',
  'SET_TODAY',
  'UPDATE_POPUP_OPEN',
  'UPDATE_CURRENT_EDIT_LEVEL',
  'UPDATE_CURRENT_HELP_PAGE',
  'UPDATE_SELECTED_START_TASK',
  'UPDATE_CALENDAR_WEEK',
  'UPDATE_STAFFING_CALENDAR_YEAR',
  'UPDATE_CURRENT_TUTORIAL_PARTICIPANT_ID',
  'UPDATE_CURRENT_TUTORIAL_PARTICIPANT_STATE',
  'CACHE_TUTORIAL_CONTENT',
];

function saveToCache(state: State, cacheFn, action) {
  const organizationId = state.id;
  const username = state.username;
  if (username !== undefined && username !== null && organizationId !== undefined && organizationId !== null) {
    const keys = {
      user: username,
      organization: username + '-organization-' + organizationId,
      pageGroups: username + '-page-groups-' + organizationId,
      references: username + '-references-' + organizationId,
    };
    const reducersToPersist = Object.keys(keys) as Array<keyof typeof keys>;
    ric(
      () => {
        trace('store.saveToCache', () => Promise.all(reducersToPersist.map((key) => cacheFn(keys[key], state[key]))), {
          'action.type': action.type,
        });
      },
      { timeout: 500 },
    );
  }
}

const debouncedSaveToCache = debounce((state, cacheFn, action) => saveToCache(state, cacheFn, action), 3000, {
  trailing: true,
  leading: false,
});

const cacheMiddleware = ({ cacheFn }) => {
  return ({ getState }) =>
    (next) =>
    (action) => {
      const res = next(action);
      const state = getState();
      if (IS_BROWSER && !ignoreActions.includes(action.type)) {
        debouncedSaveToCache(state, cacheFn, action);
      }
      return res;
    };
};

export const cache = getConfiguredCache({
  version: '2',
  maxAge: ms.days(14),
});

const cachingMiddleware = cacheMiddleware({ cacheFn: cache.set });

const initialState: State = {
  organization: undefined,
  user: undefined,
  id: null,
  loaded: false,
  loading: false,
  error: false,
  references: [],
  session: {},
  popupOpen: false,
  currentEditLevel: 0,
  helpViewerOpen: false,
  tutorialViewerOpen: false,
  currentTutorialParticipantState: undefined,
  tutorialContentCache: {},
  sideContentOpen: false,
  currentHelpPage: '',
  currentSideContentTemplateId: 0,
  copilotOpen: false,
  chatOpen: false,
  chatMode: 'ai',
  chatExpanded: false,
  matrixReady: false,
  matrixRooms: [],
  matrixCurrentRoomId: null,
  matrixUnreadCount: 0,
  matrixUserPresence: {},
  weekStart: mondayThisWeek().toString(),
  singleUserView: true,
  staffingCalendarYear: LocalDate.now().year().toString(),
  path: '',
  queryParams: {},
  pageGroups: [],
  key: '',
  selectedStartTask: '',
  today: LocalDate.now().toString(),
};

export const store = configureStore({
  reducer: reducer,
  preloadedState: initialState,
  middleware: (gDM) => {
    return gDM().prepend(checklistPendingMiddleware).concat(cachingMiddleware);
  },
});

export function getStore() {
  return store;
}

export function createUuid(): string {
  return uuid();
}

setInterval(
  () => {
    const t = store.getState().today;
    const n = LocalDate.now().toString();
    if (n !== t) {
      store.dispatch(setToday(LocalDate.now()));
    }
  },
  5 * 60 * 1000,
);

export { endYear } from './config';
